import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext.js';
import { AppEntryRedirect } from './components/AppEntryRedirect.js';
import { AppLayout } from './components/AppLayout.js';
import { PlatformLayout } from './components/PlatformLayout.js';
import { PlatformProtectedRoute } from './components/PlatformProtectedRoute.js';
import { ProtectedRoute } from './components/ProtectedRoute.js';
import { LoginPage } from './pages/LoginPage.js';
import { NoTenantAccessPage } from './pages/NoTenantAccessPage.js';
import { LandingPage } from './pages/public/LandingPage.js';
import { PrivacyPolicyPage } from './pages/public/PrivacyPolicyPage.js';
import { RequestDemoPage } from './pages/public/RequestDemoPage.js';
import { SupportPage } from './pages/public/SupportPage.js';
import { TermsOfUsePage } from './pages/public/TermsOfUsePage.js';
import { PlatformOrganizationDetailPage } from './pages/platform/PlatformOrganizationDetailPage.js';
import { PlatformOrganizationsPage } from './pages/platform/PlatformOrganizationsPage.js';
import { PlatformOverviewPage } from './pages/platform/PlatformOverviewPage.js';
import { PlatformUserDetailPage } from './pages/platform/PlatformUserDetailPage.js';
import { PlatformUsersPage } from './pages/platform/PlatformUsersPage.js';
import { InstructorCasesPage } from './pages/instructor/InstructorCasesPage.js';
import { InstructorDashboardPage } from './pages/instructor/InstructorDashboardPage.js';
import { InstructorStudentsPage } from './pages/instructor/InstructorStudentsPage.js';
import { FacultySubmissionReviewPage } from './pages/instructor/FacultySubmissionReviewPage.js';
import { ReviewQueuePage } from './pages/instructor/ReviewQueuePage.js';
import { StudentDocumentsPage as InstructorStudentDocumentsPage } from './pages/shared/StudentDocumentsPage.js';
import { ClinicalSitesPage } from './pages/pd/ClinicalSitesPage.js';
import { CohortsPage } from './pages/pd/CohortsPage.js';
import { InstructorsPage } from './pages/pd/InstructorsPage.js';
import { PDDashboardPage } from './pages/pd/PDDashboardPage.js';
import { ProceduresPage } from './pages/pd/ProceduresPage.js';
import { ProgramSettingsPage } from './pages/pd/ProgramSettingsPage.js';
import { RequirementsPage } from './pages/pd/RequirementsPage.js';
import { SpecialtiesPage } from './pages/pd/SpecialtiesPage.js';
import { StudentsPage } from './pages/pd/StudentsPage.js';
import { StudentDocumentsPage as PDStudentDocumentsPage } from './pages/shared/StudentDocumentsPage.js';
import { CasesPage } from './pages/student/CasesPage.js';
import { ClinicalSheetsPage } from './pages/student/ClinicalSheetsPage.js';
import { StudentSubmissionReviewPage } from './pages/student/StudentSubmissionReviewPage.js';
import { ProgressPage } from './pages/student/ProgressPage.js';
import { StudentDashboardPage } from './pages/student/StudentDashboardPage.js';

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/request-demo" element={<RequestDemoPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfUsePage />} />
          <Route path="/support" element={<SupportPage />} />

          <Route path="/login" element={<LoginPage />} />
          <Route path="/app" element={<AppEntryRedirect />} />
          <Route path="/no-access" element={<NoTenantAccessPage />} />

          <Route
            path="/platform"
            element={
              <PlatformProtectedRoute>
                <PlatformLayout />
              </PlatformProtectedRoute>
            }
          >
            <Route index element={<PlatformOverviewPage />} />
            <Route path="organizations" element={<PlatformOrganizationsPage />} />
            <Route path="organizations/:organizationId" element={<PlatformOrganizationDetailPage />} />
            <Route path="users" element={<PlatformUsersPage />} />
            <Route path="users/:userId" element={<PlatformUserDetailPage />} />
          </Route>

          <Route
            path="/student"
            element={
              <ProtectedRoute roles={['STUDENT']}>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<StudentDashboardPage />} />
            <Route path="sheets" element={<ClinicalSheetsPage />} />
            <Route path="sheets/:id/review" element={<StudentSubmissionReviewPage />} />
            <Route path="cases" element={<CasesPage />} />
            <Route path="progress" element={<ProgressPage />} />
          </Route>

          <Route
            path="/instructor"
            element={
              <ProtectedRoute roles={['INSTRUCTOR']}>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<InstructorDashboardPage />} />
            <Route path="review" element={<ReviewQueuePage />} />
            <Route path="review/:id" element={<FacultySubmissionReviewPage />} />
            <Route path="students" element={<InstructorStudentsPage />} />
            <Route
              path="students/:studentId/documents"
              element={<InstructorStudentDocumentsPage backPath="/instructor/students" backLabel="Students" />}
            />
            <Route path="cases" element={<InstructorCasesPage />} />
          </Route>

          <Route
            path="/pd"
            element={
              <ProtectedRoute roles={['PROGRAM_DIRECTOR']}>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<PDDashboardPage />} />
            <Route path="cohorts" element={<CohortsPage />} />
            <Route path="students" element={<StudentsPage />} />
            <Route
              path="students/:studentId/documents"
              element={<PDStudentDocumentsPage backPath="/pd/students" backLabel="Students" />}
            />
            <Route path="instructors" element={<InstructorsPage />} />
            <Route path="sites" element={<ClinicalSitesPage />} />
            <Route path="specialties" element={<SpecialtiesPage />} />
            <Route path="procedures" element={<ProceduresPage />} />
            <Route path="requirements" element={<RequirementsPage />} />
            <Route path="settings" element={<ProgramSettingsPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
